<script setup>
import { ref, computed, onMounted } from 'vue';
import API from '@util/API';
import { useTranslation } from '../../composables/useTranslation';

const { t } = useTranslation();

const props = defineProps({
    integration: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['connected', 'disconnected']);

const apiKey = ref('');
const savedApiKey = ref(''); // Store the original saved API key
const isConnecting = ref(false);
const isRemoving = ref(false);
const showRemoveConfirmation = ref(false);
const connectionStatus = ref(null); // null, 'success', 'error'
const errorMessage = ref('');

const maskedApiKey = computed(() => {
    if (!savedApiKey.value) {
        return apiKey.value;
    }
    // Mask the API key: show first 4 chars + ***** + last 4 chars
    const key = savedApiKey.value;
    if (key.length <= 8) return key;
    return key.substring(0, 4) + '*'.repeat(key.length - 8) + key.substring(key.length - 4);
});

const loadExistingApiKey = async () => {
    // Try to load existing API key from WordPress options
    try {
        const response = await API.get('integrations/get_mailchimp_key');
        if (response.data && response.data.api_key) {
            apiKey.value = response.data.api_key;
            savedApiKey.value = response.data.api_key;
            connectionStatus.value = 'success';

            // Emit connected event to update the integration store
            emit('connected', {
                api_key: response.data.api_key
            });
        }
    } catch (error) {
        console.log('No existing API key found');
    }
};

const connectMailchimp = async () => {
    if (!apiKey.value || !apiKey.value.trim()) {
        return;
    }

    isConnecting.value = true;
    connectionStatus.value = null;
    errorMessage.value = '';

    try {
        // Save API key to WordPress options first
        const saveResponse = await API.post('integrations/save_mailchimp_key', {
            api_key: apiKey.value.trim()
        });

        if (!saveResponse.data || !saveResponse.data.success) {
            throw new Error('Failed to save API key');
        }

        // Small delay to ensure database write completes
        await new Promise(resolve => setTimeout(resolve, 100));

        // Test connection by fetching metadata
        const response = await API.get('integrations/mailchimp_meta');

        if (response.data && response.data.success) {
            connectionStatus.value = 'success';

            // Update saved API key
            savedApiKey.value = apiKey.value;

            // Enable the integration
            await API.post('integrations', {
                integration_id: 'mailchimp',
                enabled: true
            });

            // Emit success event to parent/store
            emit('connected', {
                api_key: apiKey.value,
                lists: response.data.lists || [],
                tags: response.data.tags || [],
                fields: response.data.fields || []
            });
        } else {
            connectionStatus.value = 'error';
            errorMessage.value = response.data?.message || t('connection_failed');
        }
    } catch (error) {
        connectionStatus.value = 'error';
        errorMessage.value = error.response?.data?.message || error.message || t('connection_failed');
        console.error('Connection error:', error);
    } finally {
        isConnecting.value = false;
    }
};

const confirmRemoveApiKey = async () => {
    isRemoving.value = true;
    errorMessage.value = '';

    try {
        // Remove API key from WordPress options
        await API.post('integrations/remove_mailchimp_key');

        // Disable the integration
        await API.post('integrations', {
            integration_id: 'mailchimp',
            enabled: false
        });

        // Reset local state
        apiKey.value = '';
        savedApiKey.value = '';
        connectionStatus.value = null;
        showRemoveConfirmation.value = false;

        // Emit disconnected event
        emit('disconnected');
    } catch (error) {
        connectionStatus.value = 'error';
        errorMessage.value = error.response?.data?.message || error.message || 'Failed to remove API key.';
        console.error('API key removal error:', error);
    } finally {
        isRemoving.value = false;
    }
};

onMounted(() => {
    loadExistingApiKey();
});
</script>

<template>
  <div class="bg-white">
    <!-- Header Section with Yellow Background -->
    <div class="bg-gradient-to-r from-yellow-100 to-yellow-200 px-8 py-6 text-center">
      <div class="flex items-center justify-center gap-3">
        <!-- Mailchimp Logo -->
        <div class="rounded-full p-2">
          <img :src="Image('integrations/mailchimp.png')" class="w-16 h-auto" alt="Mailchimp">
        </div>
        <h1 class="text-3xl text-gray-800 font-bold">
          Mailchimp
        </h1>
      </div>
    </div>

    <!-- Content Section -->
    <div class="px-8 py-8">
      <!-- Title -->
      <h2 class="text-2xl font-semibold text-gray-800 mb-2">{{ t('connect_mailchimp') }}</h2>

      <!-- Description -->
      <p class="text-gray-600 mb-6 leading-relaxed" v-html="t('mailchimp_description', {
          account: `<a href='https://mailchimp.com/login/' target='_blank' class='text-blue-600 hover:underline font-semibold'>Mailchimp account</a>`
      })"></p>

      <!-- Watch Tutorial Button -->
      <div class="mb-8">
        <a
          href="https://youtu.be/x70qZf2KWwg"
          target="_blank"
          class="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 transition duration-150 rounded-full px-4 py-2 border border-gray-200 text-sm font-medium text-gray-700"
        >
          <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 48 48">
            <path fill="#FF3D00" d="M43.2,33.9c-0.4,2.1-2.1,3.7-4.2,4c-3.3,0.5-8.8,1.1-15,1.1c-6.1,0-11.6-0.6-15-1.1c-2.1-0.3-3.8-1.9-4.2-4C4.4,31.6,4,28.2,4,24c0-4.2,0.4-7.6,0.8-9.9c0.4-2.1,2.1-3.7,4.2-4C12.3,9.6,17.8,9,24,9c6.2,0,11.6,0.6,15,1.1c2.1,0.3,3.8,1.9,4.2,4c0.4,2.3,0.9,5.7,0.9,9.9C44,28.2,43.6,31.6,43.2,33.9z"></path>
            <path fill="#FFF" d="M20 31L20 17 32 24z"></path>
          </svg>
          {{ t('watch_tutorial') }}
        </a>
      </div>

      <!-- API Key Section -->
      <div class="mb-6">
        <div class="flex items-center gap-2 mb-3">
          <label class="text-gray-800 font-semibold text-base">{{ t('api_key') }}</label>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600"
            :title="t('mailchimp_api_key_tooltip')"
          >
            <svg class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
        <p class="text-sm text-gray-500 mb-3" v-html="t('mailchimp_api_key_help', {
            link: `<a href='https://mailchimp.com/help/about-api-keys/' target='_blank' class='text-blue-600 hover:underline'>Read how to get it →</a>`
        })"></p>

        <!-- API Key Input with Inline Button -->
        <div class="flex gap-2">
          <div class="relative flex-1">
            <input
              v-if="!savedApiKey"
              v-model="apiKey"
              type="text"
              :placeholder="t('mailchimp_api_placeholder')"
              class="w-full px-4 py-3 border-2 border-blue-500 rounded-lg focus:outline-none focus:border-blue-600 font-mono text-sm"
              :disabled="isConnecting"
            />
            <input
              v-else
              type="text"
              :value="maskedApiKey"
              disabled
              class="w-full px-4 py-3 border-2 border-blue-500 rounded-lg focus:outline-none focus:border-blue-600 font-mono text-sm bg-gray-50"
            />
          </div>

          <!-- Connect/Remove Button -->
          <button
            v-if="!savedApiKey"
            @click="connectMailchimp"
            :disabled="!apiKey || isConnecting"
            class="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 w-36"
          >
            <span v-if="isConnecting" class="inline-block w-4 h-4">
              <svg class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
            <span v-if="!isConnecting">{{ t('connect') }}</span>
            <span v-else>{{ t('connecting') }}</span>
          </button>

          <button
            v-else
            @click="showRemoveConfirmation = true"
            type="button"
            class="bg-red-50 hover:bg-red-100 text-red-600 font-semibold px-6 py-3 rounded-lg transition duration-150 flex items-center justify-center gap-2 w-36"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
            {{ t('remove') }}
          </button>
        </div>
      </div>

      <!-- Connection Status Messages -->
      <div v-if="connectionStatus === 'success'" class="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
        <svg class="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <p class="text-green-800 text-sm font-medium">{{ t('connected_successfully') }}</p>
      </div>

      <div v-if="connectionStatus === 'error'" class="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
        <svg class="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
        <div class="flex-1">
          <p class="text-red-800 text-sm font-medium">{{ errorMessage || t('connection_failed') }}</p>
        </div>
      </div>

      <!-- Confirmation Dialog -->
      <div v-if="showRemoveConfirmation" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
          <h3 class="text-xl font-semibold text-gray-800 mb-3">{{ t('remove_api_key') }}</h3>
          <p class="text-gray-600 mb-6">{{ t('remove_api_key_confirmation') }}</p>
          <div class="flex justify-end gap-3">
            <button
              @click="showRemoveConfirmation = false"
              :disabled="isRemoving"
              class="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-6 py-3 rounded-lg transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ t('cancel') }}
            </button>
            <button
              @click="confirmRemoveApiKey"
              :disabled="isRemoving"
              class="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-lg transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <span v-if="isRemoving" class="inline-block w-5 h-5">
                <svg class="animate-spin w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
              <span v-if="!isRemoving">{{ t('yes_remove_it') }}</span>
              <span v-else>{{ t('removing') }}</span>
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
/* Additional custom styles if needed */
</style>
